import { Tooltip, IconButton, Box, CircularProgress } from '@mui/material';

import { ROW_ACTIONS_SX } from '@/shared/constants/tableIconButtons';
import { Edit, Send, NoAccounts, PersonAddAlt } from '@mui/icons-material';
// Trash2 rather than MUI's filled Delete, to match the kill action in Manage
// CXVH Sessions. lucide needs an explicit size — ROW_ACTIONS_SX sizes via
// `.MuiSvgIcon-root`, which a plain <svg> does not carry.
import { Trash2 } from 'lucide-react';
import { useState } from 'react';

interface Props {
  currentUser: { id: number };
  user: { id: number; disabled?: boolean };
  onEdit: () => void | Promise<void>;
  onDelete: () => void | Promise<void>;
  onDisable: () => void | Promise<void>;
  onInvite: () => void | Promise<void>;
  invitees?: boolean;
}

const ActionMenu = ({
  currentUser,
  user,
  onEdit,
  onDelete,
  onDisable,
  onInvite,
  invitees = false
}: Props) => {
  const isCurrentUser = currentUser.id === user.id;
  
  // Loading states for each action
  const [isEditLoading, setIsEditLoading] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [isDisableLoading, setIsDisableLoading] = useState(false);
  const [isInviteLoading, setIsInviteLoading] = useState(false);

  // Wrapper functions to handle loading states
  const handleEdit = async () => {
    try {
      setIsEditLoading(true);
      await onEdit();
    } catch (error) {
      console.error('Error editing user:', error);
    } finally {
      setIsEditLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setIsDeleteLoading(true);
      await onDelete();
    } catch (error) {
      console.error('Error deleting user:', error);
    } finally {
      setIsDeleteLoading(false);
    }
  };

  const handleDisable = async () => {
    try {
      setIsDisableLoading(true);
      await onDisable();
    } catch (error) {
      console.error('Error toggling user access:', error);
    } finally {
      setIsDisableLoading(false);
    }
  };

  const handleInvite = async () => {
    try {
      setIsInviteLoading(true);
      await onInvite();
    } catch (error) {
      console.error('Error resending invite:', error);
    } finally {
      setIsInviteLoading(false);
    }
  };

  return (
    <Box sx={ROW_ACTIONS_SX}>
      {/* Resend Invite Action - Only visible for invitees */}
      {invitees && (
        <Tooltip title="Resend Invite">
          <span>
            <IconButton
              disabled={isCurrentUser || isInviteLoading}
              onClick={handleInvite}
            >
              {isInviteLoading ? (
                <CircularProgress size={16} />
              ) : (
                <Send />
              )}
            </IconButton>
          </span>
        </Tooltip>
      )}

      {/* Edit User Action */}
      <Tooltip title="Edit User">
        <span>
          <IconButton
            disabled={isCurrentUser || isEditLoading}
            onClick={handleEdit}
          >
            {isEditLoading ? (
              <CircularProgress size={16} />
            ) : (
              <Edit />
            )}
          </IconButton>
        </span>
      </Tooltip>

      {/* Enable/Disable User Action */}
      <Tooltip title={`${user.disabled ? 'Enable' : 'Disable'} Access for User`}>
        <span>
          <IconButton
            disabled={isCurrentUser || isDisableLoading}
            onClick={handleDisable}
          >
            {isDisableLoading ? (
              <CircularProgress size={16} />
            ) : user.disabled ? (
              <PersonAddAlt />
            ) : (
              <NoAccounts />
            )}
          </IconButton>
        </span>
      </Tooltip>

      {/* Delete User Action */}
      <Tooltip title="Remove User">
        <span>
          <IconButton
            disabled={isCurrentUser || isDeleteLoading}
            onClick={handleDelete}
            color={isCurrentUser ? 'default' : 'error'}
          >
            {isDeleteLoading ? (
              <CircularProgress size={16} color="error" />
            ) : (
              <Trash2 size={16} />
            )}
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};

export default ActionMenu;